import { useEffect, useRef, useState } from 'react';
import {
  VehicleLocalStorage,
  VehiculeCheckFilterType,
} from '@/entities/vehicle';
import { useLocalStorageState } from '../local-storage.hooks';
import debounce from 'lodash/debounce';
import { Dayjs } from 'dayjs';

interface VehicleFilterProps {
  setCurrentPage: (page: number) => void;
}

export const useVehicleFilter = ({ setCurrentPage }: VehicleFilterProps) => {
  const [debouncedFilter, setDebouncedFilter] =
    useState<VehiculeCheckFilterType>({ key: '' });
  const filtersEmptyRef = useRef<boolean>(true);

  /**
   * === Filter section ===
   */
  const [key, setKey] = useLocalStorageState({
    key: VehicleLocalStorage.KEY,
    removeOnClose: true,
  });

  const [status, setStatus] = useLocalStorageState({
    key: VehicleLocalStorage.STATUS,
    removeOnClose: true,
  });

  const [startDate, setStartDate] = useLocalStorageState({
    key: VehicleLocalStorage.START_DATE,
    removeOnClose: true,
  });

  const [endDate, setEndDate] = useLocalStorageState({
    key: VehicleLocalStorage.END_DATE,
    removeOnClose: true,
  });

  const [requestInspection, setRequestInspection] = useLocalStorageState({
    key: VehicleLocalStorage.REQUEST_INSPECTION,
    removeOnClose: true,
  });

  const [groupId, setGroupId] = useLocalStorageState({
    key: VehicleLocalStorage.GROUP_ID,
    removeOnClose: true,
  });

  const [companyId, setCompanyId] = useLocalStorageState({
    key: VehicleLocalStorage.COMPANY_ID,
    removeOnClose: true,
  });

  useEffect(() => {
    setDebouncedFilter((prev) => ({
      ...prev,
      key: key || '',
      status,
      startDate,
      endDate,
      requestInspection: requestInspection === 'true',
      groupId,
      companyId,
    }));

    filtersEmptyRef.current =
      !key &&
      !status &&
      !startDate &&
      !endDate &&
      requestInspection !== 'true' &&
      !groupId &&
      !companyId;
  }, [key, status, startDate, endDate, requestInspection, groupId, companyId]);

  const debounceFilterUpdate = (value: string) => {
    setKey(value);
    debounce((value: string) => {
      setDebouncedFilter((prev) => ({ ...prev, key: value }));
    }, 1000);
  };

  const searchByStatus = (value: string) => {
    setStatus(value);
    setDebouncedFilter((prev) => ({ ...prev, status: value }));
  };

  const searchBeginDate = (value: Dayjs) => {
    setStartDate(value?.format('YYYY-MM-DD'));
    setDebouncedFilter((prev) => ({
      ...prev,
      startDate: value?.format('YYYY-MM-DD'),
    }));
  };

  const searchEndDate = (value: Dayjs) => {
    setEndDate(value?.format('YYYY-MM-DD'));
    setDebouncedFilter((prev) => ({
      ...prev,
      endDate: value?.format('YYYY-MM-DD'),
    }));
  };

  const searchByRequestInspect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.checked;
    setRequestInspection(value ? 'true' : 'false');
    setDebouncedFilter((prev) => ({ ...prev, requestInspection: value }));
  };

  const searchByGroup = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setGroupId(e.target.value);
    setDebouncedFilter((prev) => ({ ...prev, groupId: e.target.value }));
  };

  const searchByCompany = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setCompanyId(e.target.value);
    setDebouncedFilter((prev) => ({ ...prev, companyId: e.target.value }));
  };

  const clearFilters = () => {
    setKey('');
    setStatus('');
    setStartDate('');
    setEndDate('');
    setRequestInspection('false');
    setGroupId('');
    setCompanyId('');
    setCurrentPage(1);
  };
  /**
   * === END OF Filter section ===
   */

  return {
    debouncedFilter,
    setDebouncedFilter,
    filtersEmpty: filtersEmptyRef.current,
    clearFilters,
    key,
    status,
    startDate,
    endDate,
    requestInspection,
    groupId,
    companyId,
    debounceFilterUpdate,
    searchByStatus,
    searchBeginDate,
    searchEndDate,
    searchByRequestInspect,
    searchByGroup,
    searchByCompany,
  };
};
